import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CalendarView } from '../components/calendar/CalendarView';
import { Filter, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CalendarPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState({});
  const [genres, setGenres] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('month');
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [showFilters, setShowFilters] = useState(false);

  const selectedGenre = searchParams.get('genre') || '';
  const selectedCity = searchParams.get('city') || '';
  const selectedType = searchParams.get('type') || '';

  const activeFiltersCount = [selectedGenre, selectedCity, selectedType].filter(Boolean).length;

  useEffect(() => {
    fetchFilters();
  }, []);

  useEffect(() => {
    fetchCalendarEvents();
  }, [currentMonth, currentYear, selectedGenre, selectedCity, selectedType]);

  const fetchFilters = async () => {
    try {
      const [genresRes, citiesRes] = await Promise.all([
        axios.get(`${API}/genres`),
        axios.get(`${API}/cities`)
      ]);
      setGenres(genresRes.data);
      setCities(citiesRes.data);
    } catch (error) {
      console.error('Error fetching filters:', error);
    }
  };

  const fetchCalendarEvents = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('month', currentMonth);
      params.append('year', currentYear);
      if (selectedGenre) params.append('genre', selectedGenre);
      if (selectedCity) params.append('city', selectedCity);
      if (selectedType) params.append('event_type', selectedType);

      const response = await axios.get(`${API}/calendar/events?${params.toString()}`);
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching calendar events:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  const clearAllFilters = () => {
    setSearchParams({});
  };

  const handleMonthChange = useCallback((month, year) => {
    setCurrentMonth(month);
    setCurrentYear(year);
  }, []);

  return (
    <div className="min-h-screen pt-24" data-testid="calendar-page">
      {/* Header */}
      <div className="section-container py-12 border-b border-white/5">
        <span className="text-[var(--primary)] text-xs uppercase tracking-[0.3em] block mb-4">
          Programa
        </span>
        <h1 className="font-['Playfair_Display'] text-5xl md:text-6xl text-white mb-6">
          Calendario de Eventos
        </h1>
        <p className="text-white/50 max-w-2xl">
          Consulta la programación completa y no te pierdas ningún evento.
        </p>
      </div>

      {/* Filters Section */}
      <div className="section-container py-6 border-b border-white/5">
        <div className="flex flex-col gap-4">
          {/* Filter Toggle & Active Filters */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 border transition-colors ${
                showFilters || activeFiltersCount > 0
                  ? 'border-[var(--primary)] text-[var(--primary)]'
                  : 'border-white/20 text-white/70 hover:border-white/40'
              }`}
            >
              <Filter size={18} />
              Filtros
              {activeFiltersCount > 0 && (
                <span className="ml-1 w-5 h-5 rounded-full bg-[var(--primary)] text-black text-xs flex items-center justify-center">
                  {activeFiltersCount}
                </span>
              )}
            </button>

            {activeFiltersCount > 0 && (
              <button
                onClick={clearAllFilters}
                className="text-white/50 hover:text-white text-sm flex items-center gap-1"
              >
                <X size={14} />
                Limpiar filtros
              </button>
            )}
          </div>

          {/* Filter Options */}
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-white/5 border border-white/10 animate-fadeIn">
              {/* Genre Filter */}
              <div>
                <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">
                  Género
                </label>
                <select
                  value={selectedGenre}
                  onChange={(e) => updateFilter('genre', e.target.value)}
                  className="w-full bg-transparent border border-white/20 text-white p-3 focus:border-[var(--primary)] focus:outline-none"
                >
                  <option value="" className="bg-[var(--bg)]">Todos los géneros</option>
                  {genres.map(genre => (
                    <option key={genre.id} value={genre.value} className="bg-[var(--bg)]">
                      {genre.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* City Filter */}
              <div>
                <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">
                  Ciudad
                </label>
                <select
                  value={selectedCity}
                  onChange={(e) => updateFilter('city', e.target.value)}
                  className="w-full bg-transparent border border-white/20 text-white p-3 focus:border-[var(--primary)] focus:outline-none"
                >
                  <option value="" className="bg-[var(--bg)]">Todas las ciudades</option>
                  {cities.map(city => (
                    <option key={city.id} value={city.value} className="bg-[var(--bg)]">
                      {city.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Event Type Filter */}
              <div>
                <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">
                  Tipo
                </label>
                <select
                  value={selectedType}
                  onChange={(e) => updateFilter('type', e.target.value)}
                  className="w-full bg-transparent border border-white/20 text-white p-3 focus:border-[var(--primary)] focus:outline-none"
                >
                  <option value="" className="bg-[var(--bg)]">Todos los tipos</option>
                  <option value="directo" className="bg-[var(--bg)]">🔴 En Directo</option>
                  <option value="grabado" className="bg-[var(--bg)]">📹 Grabado</option>
                </select>
              </div>
            </div>
          )}

          {/* Active Filter Tags */}
          {activeFiltersCount > 0 && (
            <div className="flex flex-wrap gap-2">
              {selectedGenre && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-[var(--primary)]/20 text-[var(--primary)] text-sm">
                  {genres.find(g => g.value === selectedGenre)?.label || selectedGenre}
                  <button onClick={() => updateFilter('genre', '')} className="hover:text-white">
                    <X size={14} />
                  </button>
                </span>
              )}
              {selectedCity && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-[var(--primary)]/20 text-[var(--primary)] text-sm">
                  {cities.find(c => c.value === selectedCity)?.label || selectedCity}
                  <button onClick={() => updateFilter('city', '')} className="hover:text-white">
                    <X size={14} />
                  </button>
                </span>
              )}
              {selectedType && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-[var(--primary)]/20 text-[var(--primary)] text-sm">
                  {selectedType === 'directo' ? '🔴 En Directo' : '📹 Grabado'}
                  <button onClick={() => updateFilter('type', '')} className="hover:text-white">
                    <X size={14} />
                  </button>
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Calendar */}
      <div className="section-container section-spacing">
        {loading ? (
          <div className="calendar-container p-6">
            <div className="h-[600px] skeleton" />
          </div>
        ) : (
          <CalendarView
            events={events}
            view={view}
            onViewChange={setView}
            onMonthChange={handleMonthChange}
            initialMonth={currentMonth}
            initialYear={currentYear}
          />
        )}
      </div>
    </div>
  );
};

export default CalendarPage;
